/**
 * useSidebarResize -- Hook for drag-resizing any sidebar on desktop.
 *
 * Behavior:
 * - Listens for mousedown on a handle element, then mousemove/mouseup on document.
 * - Clamps width between minWidth and maxWidth (configurable, default 240-400px).
 * - Double-click on handle resets to default (configurable, default 280px).
 * - Uses requestAnimationFrame during drag for smooth updates.
 * - Persists width to localStorage under a configurable key (default 'sidebar_width').
 *
 * Usage:
 *   const { width, handleProps, isResizing } = useSidebarResize();
 *   <aside style={{ width: `${width}px` }}> ... </aside>
 *   <div {...handleProps} />
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_STORAGE_KEY = 'opus-ia-sidebar-width';
const DEFAULT_WIDTH = 260;
const MIN_WIDTH = 220;
const MAX_WIDTH = 380;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getStoredWidth(
  storageKey: string,
  defaultWidth: number,
  minWidth: number,
  maxWidth: number,
): number {
  if (typeof window === 'undefined') return defaultWidth;
  const stored = localStorage.getItem(storageKey);
  if (!stored) return defaultWidth;
  const parsed = parseInt(stored, 10);
  if (isNaN(parsed) || parsed < minWidth || parsed > maxWidth) return defaultWidth;
  return parsed;
}

function storeWidth(storageKey: string, width: number): void {
  localStorage.setItem(storageKey, String(width));
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseSidebarResizeOptions {
  /** Whether resize functionality is enabled (should be false on mobile/tablet) */
  enabled?: boolean;
  /** localStorage key for persisting width (default: 'sidebar_width') */
  storageKey?: string;
  /** Minimum allowed width in px (default: 240) */
  minWidth?: number;
  /** Maximum allowed width in px (default: 400) */
  maxWidth?: number;
  /** Default/reset width in px (default: 280) */
  defaultWidth?: number;
}

interface UseSidebarResizeReturn {
  /** Current sidebar width in pixels */
  width: number;
  /** Whether the user is actively dragging */
  isResizing: boolean;
  /** Props to spread on the resize handle element */
  handleProps: {
    onMouseDown: (e: React.MouseEvent) => void;
    onDoubleClick: () => void;
  };
}

export function useSidebarResize(
  options: UseSidebarResizeOptions = {},
): UseSidebarResizeReturn {
  const {
    enabled = true,
    storageKey = DEFAULT_STORAGE_KEY,
    minWidth = MIN_WIDTH,
    maxWidth = MAX_WIDTH,
    defaultWidth = DEFAULT_WIDTH,
  } = options;

  const [width, setWidth] = useState(() =>
    getStoredWidth(storageKey, defaultWidth, minWidth, maxWidth),
  );
  const [isResizing, setIsResizing] = useState(false);

  // Refs to avoid stale closures in global event handlers
  const widthRef = useRef(width);
  const rafRef = useRef<number | null>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Sync ref with state
  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  // ---- Mouse move handler (during drag) ----
  const handleMouseMove = useCallback((e: MouseEvent) => {
    e.preventDefault();
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      const delta = e.clientX - startXRef.current;
      const newWidth = Math.min(maxWidth, Math.max(minWidth, startWidthRef.current + delta));
      setWidth(newWidth);
      rafRef.current = null;
    });
  }, [minWidth, maxWidth]);

  // ---- Mouse up handler (end drag) ----
  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    // Cancel any pending rAF
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    // Persist final width
    storeWidth(storageKey, widthRef.current);

    // Clean up listeners
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove, storageKey]);

  // ---- Mouse down handler (start drag) ----
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!enabled) return;
      e.preventDefault();
      e.stopPropagation();

      startXRef.current = e.clientX;
      startWidthRef.current = widthRef.current;
      setIsResizing(true);

      // Prevent text selection during drag
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [enabled, handleMouseMove, handleMouseUp],
  );

  // ---- Double-click handler (reset to default) ----
  const handleDoubleClick = useCallback(() => {
    if (!enabled) return;
    setWidth(defaultWidth);
    storeWidth(storageKey, defaultWidth);
  }, [enabled, defaultWidth, storageKey]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return {
    width,
    isResizing,
    handleProps: {
      onMouseDown: handleMouseDown,
      onDoubleClick: handleDoubleClick,
    },
  };
}
